import { useState } from 'react';

const NAME_MAX_LENGTH = 255;

type Props = {
  /** Operator override; null inherits the pool category name. */
  name: string | null;
  /** The pool category's own name — the fallback shown when no override. */
  defaultName: string;
  onSave: (name: string) => Promise<void>;
  onReset: () => Promise<void>;
};

export function NameField({ name, defaultName, onSave, onReset }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isOverride = Boolean(name);
  const shownName = name ?? defaultName;

  const startEditing = () => {
    setDraft(shownName);
    setIsEditing(true);
  };

  const commit = async () => {
    const next = draft.trim();
    setIsEditing(false);
    if (next === '' || next === shownName) return;
    setIsSaving(true);
    try {
      await onSave(next);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setIsSaving(true);
    try {
      await onReset();
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') void commit();
    if (e.key === 'Escape') setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          autoFocus
          type="text"
          value={draft}
          maxLength={NAME_MAX_LENGTH}
          placeholder={defaultName}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => void commit()}
          aria-label="Category name"
          className="w-40 rounded border border-[var(--co-brand)] bg-[var(--co-bg)] px-1.5 py-0.5 text-[13px] text-[var(--co-text)] outline-none"
        />
        <button
          type="button"
          // preventDefault keeps the input from blurring first, so the save
          // runs once — via onClick, not via the input's onBlur.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => void commit()}
          className="shrink-0 rounded bg-[var(--co-brand)] px-2 py-0.5 text-[11px] font-semibold text-white"
        >
          OK
        </button>
      </div>
    );
  }

  return (
    <div className="group/name flex items-center gap-1">
      <h2 className="truncate text-[14px] font-medium text-[var(--co-text)]">{shownName}</h2>
      {isOverride && (
        <span className="text-[11px] text-[var(--co-muted)]" title="Default category name">
          ({defaultName})
        </span>
      )}
      <button
        type="button"
        onClick={startEditing}
        disabled={isSaving}
        title="Edit category name"
        aria-label="Edit category name"
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--co-muted)] opacity-0 transition-opacity hover:bg-[var(--co-bg)] hover:text-[var(--co-text)] group-hover/name:opacity-100"
      >
        <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M11.1 2.2a1.4 1.4 0 0 1 2 2L5.4 11.9l-2.7.7.7-2.7 7.7-7.7Z" strokeLinejoin="round" />
        </svg>
      </button>
      {isOverride && (
        <button
          type="button"
          onClick={() => void handleReset()}
          disabled={isSaving}
          title={`Reset to "${defaultName}"`}
          aria-label="Reset category name to default"
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--co-muted)] opacity-0 transition-opacity hover:bg-[var(--co-bg)] hover:text-[var(--co-text)] group-hover/name:opacity-100"
        >
          <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 8a5 5 0 1 1 1.5 3.6M3 8V4.5M3 8h3.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>
  );
}
